import { Component, OnInit, OnDestroy, inject, signal, computed, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import {
  BarrierService,
  BarrierDto,
  BarrierCommandDto,
  BarrierCommandRequest,
  DeviceStatus,
  ParkingSpaceService,
  ParkingSpaceDto,
  ParkingTypeService,
  ParkerService,
  ParkingRightService,
  ParkingRightDto,
  ParkingTypeEnum,
  ParkingRightTypeEnum,
  TimeBasedParkingRightDto,
  CreateParkingRightRequest,
  ListParkingRightsRequest,
  ListLongTermParkersRequest,
  CreateLongTermParkersRequest,
  InfrastructureService,
  InfrastructureDto,
  OperatorActionsService,
  ParkingOperatorDto,
  ParkerSiteClassificationService,
  ParkerClassification,
  ParkerDto,
  LicensePlateService,
  ListLicensePlatesRequest
} from '../../api/gen';
import { normalizePlate, Role } from 'shared-lib';
import { RoleService } from '../auth/role-service';
import { MemberRegistrationComponent } from './member-registration/member-registration.component';
import { LongTermRegistrationComponent } from './long-term-registration/long-term-registration.component';
import { EditSupplierDialogComponent, EditSupplierDialogData } from './edit-supplier-dialog/edit-supplier-dialog.component';

interface OverrideLogEntry {
  id: string;
  gateLabel: string;
  hardwareId: string;
  licensePlate: string;
  action: string;
  actionCode: string;
  status: string;
  triggerSource: string;
  isManual: boolean;
  isDetection: boolean;
  timestamp: string;
}

interface SupplierRegistryEntry {
  id: string;
  supplierName: string;
  plates: string[];
  /** Plate ids alongside their numbers, so the edit dialog can prefill without a refetch. */
  plateRefs: { id: string; fullNumber: string }[];
  comment: string;
  /**
   * Personnel/AK number lifted out of {@link comment}. The operators already type AK numbers into
   * the comment and there is existing data in that shape, so it stays the system of record —
   * this is only a read-side projection to make the column searchable and sortable.
   */
  akNumber: string;
  validFrom: string | null;
  validTo: string | null;
  zoneIds: string[];
  zonesLabel: string;
  operatorLabel: string;
  isActive: boolean;
  /** True for soft-deleted (revoked) rights — only present when "show revoked" is on. */
  isRevoked: boolean;
  /** Owning parker — the key the site classification hangs off. */
  parkerId: string | null;
  /**
   * The person this row is for. Resolved from the parker, not the right: the right's `name` is the
   * *company*, so every member of one company renders identically without this. Empty while loading
   * and for rows whose parker carries no name.
   */
  parkerName: string;
  /** Owning company. Contact person/phone live on the Organization, not on the right. */
  organizationId: string | null;
  /** Resolved from ParkerSiteClassification; null while loading or when the parker has none. */
  classification: ParkerClassification | null;
}

/** Columns the registry can be ordered by. */
type RegistrySortKey = 'name' | 'member' | 'plate' | 'status' | 'zone' | 'akNumber';
type SortDirection = 'asc' | 'desc';

/**
 * Pulls an AK/personnel number out of the free-text comment.
 *
 * Must cover what the registration form itself writes — `buildComment()` emits "AK: 904726" —
 * as well as what operators type by hand: "AK904726", "AK 904726", "AK-904726", "AKNummer: …",
 * "AK-Nr. …". An earlier version allowed only a space or hyphen after "AK" and so matched none
 * of the colon-separated values the app actually produces.
 *
 * Deliberately still anchored on a word-boundary "AK" so a phone number ("Tel: 0664…") or
 * ordinary prose never yields a bogus AK number.
 */
const AK_NUMBER_PATTERN = /\bAK(?:[-\s]*(?:nummer|nr|no)\.?)?\s*[:#\-]?\s*(\d{3,10})\b/i;

function extractAkNumber(comment: string): string {
  const match = AK_NUMBER_PATTERN.exec(comment);
  return match ? `AK${match[1]}` : '';
}

/** "Show everything" sentinel for the classification filter. */
const ALL_CLASSIFICATIONS = 'All' as const;
type ClassificationFilter = ParkerClassification | typeof ALL_CLASSIFICATIONS;

const REGISTRY_PAGE_SIZE = 5;

/** How many rights are pulled per query — one browsing page, or one side of a search. */
const REGISTRY_FETCH_SIZE = 100;

/** Plates matching the typed text, resolved before the rights they belong to are fetched. */
const PLATE_LOOKUP_SIZE = 200;

/**
 * All of the space's barriers, across every device group, in one request. Was 100 per group with
 * one request per group; now one request total, so the cap covers every group combined.
 */
const BARRIER_FETCH_SIZE = 300;

/**
 * Below this, a plate search is not worth running: one character matches a large share of every
 * plate on the site and the result is noise, not an answer.
 */
const MIN_PLATE_SEARCH_LENGTH = 2;

/**
 * Long enough that typing a whole plate produces ONE server round trip, not one per character.
 * Every search costs the shared database several queries, so the cost of waiting is the operator's
 * alone while the cost of not waiting is everybody's.
 */
const REGISTRY_SEARCH_DEBOUNCE_MS = 600;

/**
 * Below this the search never reaches the server: one or two characters match most of the site and
 * the already-loaded page is filtered in the browser instead, for free.
 */
const MIN_SERVER_SEARCH_LENGTH = 3;

/**
 * Max parallel classification lookups. The Parker contract only answers per-parker, so this is an
 * N+1 by construction and the only real defence is asking about very few parkers - see
 * `loadRegistryClassifications`, which resolves the visible page only. Removed entirely once task 21
 * option A′ lands (one query, filtered and paged in SQL).
 */
const CLASSIFICATION_FETCH_CONCURRENCY = 3;

/**
 * Every tick costs the shared database a `barriers/commands/filter` call. 12s meant ~10 calls over a
 * 2-minute observation window with nobody touching the page; 30s is still fast enough to notice a new
 * command, and the visibility check below stops it entirely while the tab is in the background.
 */
const LOG_REFRESH_INTERVAL_MS = 30000;

@Component({
  selector: 'app-hotline',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule, MatSnackBarModule, TranslatePipe, MemberRegistrationComponent, LongTermRegistrationComponent],
  templateUrl: './hotline.component.html',
  styleUrl: './hotline.component.scss'
})
export class HotlineComponent implements OnInit, OnDestroy {
  private barrierService = inject(BarrierService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private t = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private parkingSpaceService = inject(ParkingSpaceService);
  private parkingTypeService = inject(ParkingTypeService);
  private parkerService = inject(ParkerService);
  private parkingRightService = inject(ParkingRightService);
  private licensePlateService = inject(LicensePlateService);
  private classificationService = inject(ParkerSiteClassificationService);
  private infrastructureService = inject(InfrastructureService);
  private operatorActionsService = inject(OperatorActionsService);
  private roleService = inject(RoleService);

  // The currently logged-in operator — used as the operatorId when granting supplier rights.
  currentOperator = signal<ParkingOperatorDto | null>(null);

  // Restricted operator member: gate controls only — no supplier registration,
  // no detection commands in the log, no license-plate trace.
  isRestrictedMember = signal(false);

  // Barriers / gates
  barriers = signal<BarrierDto[]>([]);
  isLoadingBarriers = signal(false);
  selectedBarrierId = signal<string>('');

  // Parking-space context (for supplier credentialing)
  parkingSpace = signal<ParkingSpaceDto | null>(null);

  /**
   * Which registration form the right panel shows. A site that belongs to one organization
   * (`externalOrganizationId`, e.g. JKU) credentials company members, so it opens on the company form;
   * every other site sells individual long-term contracts and opens on the long-term form. The operator
   * can always switch — the override wins over the site default.
   */
  registrationModeOverride = signal<'longTerm' | 'company' | null>(null);
  registrationMode = computed<'longTerm' | 'company'>(() =>
    this.registrationModeOverride() ?? (this.parkingSpace()?.externalOrganizationId ? 'company' : 'longTerm'));

  // Infrastructure for the current parking space (used to resolve device-group → barriers)
  infrastructure = signal<InfrastructureDto | null>(null);

  // Zone-level infrastructures (each zone may have its own device groups with barriers)
  zoneInfrastructures = signal<InfrastructureDto[]>([]);

  // Supplier registration form
  supplierName = signal('');
  supplierPlate = signal('');
  supplierComment = signal('');
  supplierValidTo = signal<string>('');
  selectedZoneIds = signal<string[]>([]);
  isRegisteringSupplier = signal(false);

  // Zones of the selected parking space
  availableZones = computed(() => this.parkingSpace()?.parkingZones ?? []);

  // Active supplier registry
  supplierRegistry = signal<SupplierRegistryEntry[]>([]);
  isLoadingRegistry = signal(false);
  revokingId = signal<string>('');
  restoringId = signal<string>('');

  // Registry search (by license plate or supplier name) + pagination
  registrySearch = signal('');
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;
  /**
   * Increments on every registry load. A load whose token is stale by the time it returns throws its
   * result away: without this an early, slower response overwrites the newer one behind it, and the
   * operator sees results for a plate they have already finished typing.
   */
  private registryLoadToken = 0;
  /**
   * The term the server last answered for. Typing does not reach the server, so this is what tells
   * an Enter press whether there is anything new to ask about.
   */
  private lastServerSearch = '';
  registryPageIndex = signal(0);
  readonly registryPageSize = REGISTRY_PAGE_SIZE;

  classificationFilter = signal<ClassificationFilter>(ALL_CLASSIFICATIONS);
  readonly allClassifications = ALL_CLASSIFICATIONS;

  /**
   * Resolved classifications, keyed `parkerId|spaceId`. Survives registry reloads so a
   * register/revoke costs one lookup for the new parker rather than re-fetching every row.
   * Null values are cached too — "this parker has no classification" is an answer worth keeping.
   */
  private classificationCache = new Map<string, ParkerClassification | null>();

  /**
   * Chip options — the classifications this space actually uses, derived from the loaded rows.
   *
   * TEMPORARY: the real source is `ParkingSpace.AvailableClassifications` (see task 21). Deriving
   * from the data keeps this honest in the meantime — it is enum-driven, so a new classification
   * needs only a `classification.*` i18n key, never a code change here. Do NOT replace this with a
   * hardcoded Supplier/Employee/Student list.
   */
  availableClassifications = computed(() => {
    const present = new Set<ParkerClassification>();
    for (const entry of this.supplierRegistry()) {
      if (entry.classification !== null) present.add(entry.classification);
    }
    return [...present].sort();
  });

  /** Include soft-deleted (revoked) rights, so nothing is present-but-invisible. */
  showRevoked = signal(false);

  sortKey = signal<RegistrySortKey>('name');
  sortDirection = signal<SortDirection>('asc');

  /** Sort buttons rendered above the registry. */
  readonly sortableColumns: { key: RegistrySortKey; labelKey: string }[] = [
    { key: 'name', labelKey: 'hotline.sort.name' },
    { key: 'member', labelKey: 'hotline.sort.member' },
    { key: 'plate', labelKey: 'hotline.sort.plate' },
    { key: 'status', labelKey: 'hotline.sort.status' },
    { key: 'zone', labelKey: 'hotline.sort.zone' },
    { key: 'akNumber', labelKey: 'hotline.sort.akNumber' }
  ];

  filteredRegistry = computed(() => {
    const term = this.registrySearch().trim().toLowerCase();
    const classification = this.classificationFilter();
    let entries = this.supplierRegistry();

    if (classification !== ALL_CLASSIFICATIONS) {
      entries = entries.filter(e => e.classification === classification);
    }
    if (term) {
      // Comment is searched too: it is where AK numbers and contact people actually live.
      // The member's own name is included because that is what an operator on the phone is given —
      // the caller says who they are, not which company bought the right.
      // Plates are compared in canonical form (uppercase, no separators) on both sides, because
      // that is how they are stored: typing "LL-123 AB" must match the stored "LL123AB", and a row
      // the server returned for that plate must survive this filter rather than be dropped again.
      const plateTerm = normalizePlate(term);
      entries = entries.filter(e =>
        e.supplierName.toLowerCase().includes(term) ||
        e.parkerName.toLowerCase().includes(term) ||
        (plateTerm.length > 0 && e.plates.some(p => normalizePlate(p).includes(plateTerm))) ||
        e.akNumber.toLowerCase().includes(term) ||
        e.comment.toLowerCase().includes(term)
      );
    }

    return this.sortEntries(entries);
  });

  /**
   * Sorted copy — never sorts `entries` in place, which would mutate the signal's array and
   * leave the registry ordered differently from what the source says.
   */
  private sortEntries(entries: SupplierRegistryEntry[]): SupplierRegistryEntry[] {
    const key = this.sortKey();
    const factor = this.sortDirection() === 'asc' ? 1 : -1;

    return [...entries].sort((a, b) => {
      // Rows with no value sort last in both directions — an empty AK number is not "smallest",
      // it is simply unknown, and burying it keeps the populated rows together.
      const left = this.sortValue(a, key);
      const right = this.sortValue(b, key);
      if (!left && right) return 1;
      if (left && !right) return -1;

      const compared = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
      // Stable tie-break so equal keys keep a predictable order across re-sorts.
      return (compared !== 0 ? compared : a.supplierName.localeCompare(b.supplierName)) * factor;
    });
  }

  private sortValue(entry: SupplierRegistryEntry, key: RegistrySortKey): string {
    switch (key) {
      case 'member': return entry.parkerName;
      case 'plate': return entry.plates[0] === '—' ? '' : entry.plates[0] ?? '';
      case 'status': return entry.isRevoked
        ? 'revoked'
        : entry.isActive ? 'active' : 'inactive';
      case 'zone': return entry.zonesLabel;
      case 'akNumber': return entry.akNumber;
      case 'name':
      default: return entry.supplierName;
    }
  }

  /** Clicking the active column flips direction; a new column starts ascending. */
  onSort(key: RegistrySortKey): void {
    if (this.sortKey() === key) {
      this.sortDirection.update(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortKey.set(key);
      this.sortDirection.set('asc');
    }
    this.registryPageIndex.set(0);
  }

  sortIcon(key: RegistrySortKey): string {
    if (this.sortKey() !== key) return 'unfold_more';
    return this.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  async onToggleRevoked(): Promise<void> {
    this.showRevoked.update(v => !v);
    this.registryPageIndex.set(0);
    await this.loadSupplierRegistry();
  }

  registryTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredRegistry().length / this.registryPageSize))
  );

  pagedRegistry = computed(() => {
    const start = this.registryPageIndex() * this.registryPageSize;
    return this.filteredRegistry().slice(start, start + this.registryPageSize);
  });

  // Override activity log
  logEntries = signal<OverrideLogEntry[]>([]);
  isLoadingLog = signal(false);

  // Command actions
  isOpeningTemporary = signal(false);
  isOpeningPermanent = signal(false);
  isClosing = signal(false);

  isBusy = computed(() =>
    this.isOpeningTemporary() || this.isOpeningPermanent() || this.isClosing()
  );

  private barrierLabelById = new Map<string, string>();
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  private startLogPolling(): void {
    if (this.refreshTimer) return;
    this.refreshTimer = setInterval(() => this.loadLog(true), LOG_REFRESH_INTERVAL_MS);
  }

  private stopLogPolling(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Bound instance property (not a method) so it is the same reference for add/removeEventListener.
   * A minimized or backgrounded tab was still polling the database every 30s for a log nobody could
   * see; this stops that and picks up any commands issued while away on return.
   */
  private onVisibilityChange = (): void => {
    if (document.hidden) {
      this.stopLogPolling();
    } else {
      this.loadLog(true);
      this.startLogPolling();
    }
  };

  selectedBarrier = computed(() =>
    this.barriers().find(b => b.id === this.selectedBarrierId()) ?? null
  );

  async ngOnInit(): Promise<void> {
    // Resolve the restricted-member view before loading, so the first log fetch
    // is already filtered for members.
    await this.resolveRestrictedMember();

    // Resolve the acting operator so supplier rights are attributed to them.
    await this.loadCurrentOperator();

    this.loadLog();
    this.startLogPolling();
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    const parkingSpaceId = this.route.snapshot.paramMap.get('parkingSpaceId');
    if (parkingSpaceId) {
      this.loadParkingSpace(parkingSpaceId);
    }
  }

  /** A restricted member has OperatorMember but neither OperatorAdmin nor Administrator. */
  private async resolveRestrictedMember(): Promise<void> {
    const [isAdmin, isOperatorAdmin, isOperatorMember] = await Promise.all([
      this.roleService.checkAccess(Role.Administrator),
      this.roleService.checkAccess(Role.OperatorAdmin),
      this.roleService.checkAccess(Role.OperatorMember)
    ]);
    this.isRestrictedMember.set(isOperatorMember && !isOperatorAdmin && !isAdmin);
  }

  /** Fetch the logged-in operator's profile so their id can be attributed to granted rights. */
  private async loadCurrentOperator(): Promise<void> {
    try {
      const operator = await firstValueFrom(this.operatorActionsService.operatorMyAccountGet());
      this.currentOperator.set(operator ?? null);
    } catch (e) {
      console.warn('Could not load current operator:', e);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.stopLogPolling();
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
  }

  /** Return to the parking spaces page. */
  goBack(): void {
    const id = this.parkingSpace()?.id;
    this.router.navigate(id ? ['/parking-space', id] : ['/parking-space']);
  }

  barrierLabel(b: BarrierDto): string {
    const name = b.deviceName?.trim() || 'Unnamed Barrier';
    const desc = b.deviceDescription?.trim();
    return desc ? `${name} — ${desc}` : name;
  }

  onBarrierChange(id: string): void {
    this.selectedBarrierId.set(id);
    this.loadLog();
  }

  private async loadBarriers(): Promise<void> {
    this.isLoadingBarriers.set(true);
    try {
      // Collect device groups from parking space infrastructure + all zone infrastructures
      const allDeviceGroups = this.collectDeviceGroups();
      const deviceGroupIds = allDeviceGroups.map(dg => dg.id).filter((id): id is string => !!id);
      if (deviceGroupIds.length) {
        // ONE request for every group instead of one per group - a site with five zones used to open
        // this page with five `barriers/filter` calls. `pageSize` covers all groups combined now, not
        // one group, so it is raised accordingly.
        let allBarriers: BarrierDto[] = [];
        try {
          const res = await firstValueFrom(
            this.barrierService.barriersFilterPost({
              deviceGroupIds,
              pageNumber: 1,
              pageSize: BARRIER_FETCH_SIZE
            })
          );
          allBarriers = res?.items ?? [];
        } catch (e) {
          console.warn('Could not load barriers:', e);
        }
        this.barriers.set(allBarriers);
        this.barrierLabelById.clear();
        for (const b of allBarriers) {
          if (b.id) this.barrierLabelById.set(b.id, this.barrierLabel(b));
        }
        if (!this.selectedBarrierId() && allBarriers.length) {
          this.selectedBarrierId.set(allBarriers[0].id ?? '');
        }

        // Load logs for the auto-selected barrier
        this.loadLog();
      }

      // Re-resolve gate labels in any log entries loaded before the barriers arrived.
      if (this.logEntries().length) {
        this.logEntries.update(entries =>
          entries.map(e => ({ ...e, gateLabel: this.barrierLabelById.get(e.hardwareId) ?? e.gateLabel }))
        );
      }
    } catch (e) {
      console.error('Error loading barriers:', e);
      this.snackBar.open(this.t.instant('hotline.msg.loadBarriersFailed'), 'Close', { duration: 4000 });
    } finally {
      this.isLoadingBarriers.set(false);
    }
  }

  /** Aggregate device groups from the parking space's infrastructure and all its zones' infrastructures. */
  private collectDeviceGroups(): import('../../api/gen/model/deviceGroupDto').DeviceGroupDto[] {
    const groups: import('../../api/gen/model/deviceGroupDto').DeviceGroupDto[] = [];

    // Device groups from the parking space infrastructure
    const spaceInfra = this.infrastructure();
    if (spaceInfra?.deviceGroups?.length) {
      groups.push(...spaceInfra.deviceGroups);
    }

    // Device groups from each zone's infrastructure
    for (const zoneInfra of this.zoneInfrastructures()) {
      if (zoneInfra?.deviceGroups?.length) {
        groups.push(...zoneInfra.deviceGroups);
      }
    }

    return groups;
  }

  async loadLog(silent = false): Promise<void> {
    const barrierId = this.selectedBarrierId();
    if (!barrierId) return;

    if (!silent) this.isLoadingLog.set(true);
    try {
      const result = await firstValueFrom(
        this.barrierService.barriersCommandsFilterPost({ barrierId, pageNumber: 1, pageSize: 50 })
      );
      const items = result?.items ?? [];
      const entries = items
        .map(c => this.toLogEntry(c))
        // Restricted members only see non-detection (manual/system) commands.
        .filter(e => !this.isRestrictedMember() || !e.isDetection)
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      this.logEntries.set(entries);
    } catch (e) {
      console.error('Error loading override log:', e);
      if (!silent) this.snackBar.open(this.t.instant('hotline.msg.loadLogFailed'), 'Close', { duration: 4000 });
    } finally {
      if (!silent) this.isLoadingLog.set(false);
    }
  }

  private toLogEntry(c: BarrierCommandDto): OverrideLogEntry {
    const hardwareId = c.barrierId ?? '';
    const isManual = !!c.createdByOperator;
    const isDetection = !!c.createdByDetection;
    // "Done by" — who triggered the command, in human terms (never a raw UUID).
    const triggerSource = isManual
      ? (c.triggeredBy?.trim() || 'Manual Remote')
      : isDetection
        ? 'Auto-Detection'
        : (c.triggeredBy?.trim() || 'System');
    return {
      id: c.id ?? `${hardwareId}-${c.commandSentAt ?? c.createdAt ?? ''}`,
      gateLabel: this.barrierLabelById.get(hardwareId) || 'Unknown Gate',
      hardwareId: hardwareId || '—',
      licensePlate: c.licensePlate?.trim() || '—',
      actionCode: (c.commandType?.trim() || 'COMMAND').toUpperCase(),
      action: this.translateBarrierCommand((c.commandType?.trim() || 'COMMAND').toUpperCase()),
      status: c.status?.trim() ?? '',
      triggerSource,
      isManual,
      isDetection,
      timestamp: c.commandSentAt ?? c.createdAt ?? ''
    };
  }

  private translateBarrierCommand(c: string): string {
    switch (c) {
      case 'OPENPULSE': return this.t.instant('hotline.openTemporary');
      case 'OPEN': return this.t.instant('hotline.openPermanent');
      case 'CLOSE': return this.t.instant('hotline.close');
      default: return this.t.instant('hotline.openTemporary');
    }
  }
  sendOpenTemporary(): Promise<void> {
    return this.sendCommand(
      this.isOpeningTemporary,
      (id, req) => this.barrierService.barriersBarrierIdPulsePost(id, req),
      'Operator hotline: temporary open (pulse) override',
      this.t.instant('hotline.msg.openTempSent'),
      this.t.instant('hotline.msg.openTempFailed')
    );
  }

  sendOpenPermanent(): Promise<void> {
    return this.sendCommand(
      this.isOpeningPermanent,
      (id, req) => this.barrierService.barriersBarrierIdOpenPost(id, req),
      'Operator hotline: permanent open override (until manually closed)',
      this.t.instant('hotline.msg.openPermSent'),
      this.t.instant('hotline.msg.openPermFailed')
    );
  }

  sendClose(): Promise<void> {
    return this.sendCommand(
      this.isClosing,
      (id, req) => this.barrierService.barriersBarrierIdClosePost(id, req),
      'Operator hotline: close / lock override',
      this.t.instant('hotline.msg.closeSent'),
      this.t.instant('hotline.msg.closeFailed')
    );
  }

  private async sendCommand(
    flag: WritableSignal<boolean>,
    call: (id: string, req: BarrierCommandRequest) => Observable<unknown>,
    context: string,
    successMsg: string,
    errorMsg: string
  ): Promise<void> {
    const id = this.selectedBarrierId();
    if (!id) {
      this.snackBar.open(this.t.instant('hotline.msg.selectBarrierFirst'), 'Close', { duration: 3000 });
      return;
    }
    flag.set(true);
    try {
      const request: BarrierCommandRequest = { triggeredBy: 'Manual Remote', additionalContext: context };
      await firstValueFrom(call(id, request));
      this.snackBar.open(successMsg, 'Close', { duration: 3500 });
      setTimeout(() => this.loadLog(true), 600);
    } catch (e) {
      console.error(errorMsg, e);
      this.snackBar.open(errorMsg, 'Close', { duration: 4000 });
    } finally {
      flag.set(false);
    }
  }

  actionClass(action: string): string {
    const a = action.toLowerCase();
    if (a.includes('open')) return 'text-emerald-600';
    if (a.includes('close') || a.includes('lock')) return 'text-slate-600';
    return 'text-slate-500';
  }

  statusPillClass(status: DeviceStatus | undefined): string {
    switch (status) {
      case DeviceStatus.Online: return 'bg-emerald-50 text-emerald-600';
      case DeviceStatus.Offline:
      case DeviceStatus.Error: return 'bg-red-50 text-red-600';
      case DeviceStatus.Maintenance: return 'bg-amber-50 text-amber-600';
      default: return 'bg-slate-100 text-slate-500';
    }
  }

  statusDotClass(status: DeviceStatus | undefined): string {
    switch (status) {
      case DeviceStatus.Online: return 'bg-emerald-500';
      case DeviceStatus.Offline:
      case DeviceStatus.Error: return 'bg-red-500';
      case DeviceStatus.Maintenance: return 'bg-amber-500';
      default: return 'bg-slate-400';
    }
  }

  // ---- Long-term supplier credentialing ----

  isZoneSelected(zoneId: string | undefined): boolean {
    return !!zoneId && this.selectedZoneIds().includes(zoneId);
  }

  toggleZone(zoneId: string | undefined): void {
    if (!zoneId) return;
    this.selectedZoneIds.update(ids =>
      ids.includes(zoneId) ? ids.filter(id => id !== zoneId) : [...ids, zoneId]
    );
  }

  private async loadParkingSpace(id: string): Promise<void> {
    try {
      const space = await firstValueFrom(this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(id));
      this.parkingSpace.set(space ?? null);

      // The space's own infrastructure and every zone's, in ONE request. Asking per zone meant a
      // site with five zones opened the page with six infrastructure round trips instead of one.
      const spaceInfraId = space?.parkingSpaceInfrastructureId ?? null;
      const zoneInfraIds = (space?.parkingZones ?? [])
        .map(z => z.zoneInfrastructureId)
        .filter((id): id is string => !!id);
      const infraIds = [...new Set([...(spaceInfraId ? [spaceInfraId] : []), ...zoneInfraIds])];

      if (infraIds.length) {
        try {
          const infras = await firstValueFrom(
            this.infrastructureService.infrastructuresByIdsPost({ infrastructureIds: infraIds })
          ) ?? [];
          const byId = new Map(infras.filter(i => !!i.id).map(i => [i.id!, i]));

          this.infrastructure.set(spaceInfraId ? byId.get(spaceInfraId) ?? null : null);
          // Zone order is preserved: the device-group list below, and the labels built from it, read
          // in the order the operator sees the zones.
          this.zoneInfrastructures.set(
            zoneInfraIds.map(id => byId.get(id)).filter((i): i is InfrastructureDto => !!i)
          );
        } catch (e) {
          console.warn('Could not load infrastructures:', e);
          this.zoneInfrastructures.set([]);
        }
      } else {
        this.infrastructure.set(null);
        this.zoneInfrastructures.set([]);
      }

      // Now load barriers scoped to this parking space (via its device groups)
      await this.loadBarriers();
      await this.loadSupplierRegistry();
    } catch (e) {
      console.error('Error loading parking space:', e);
      this.snackBar.open(this.t.instant('hotline.msg.loadSpaceFailed'), 'Close', { duration: 4000 });
    }
  }

  /**
   * Explicit refresh. Drops the classification cache first — otherwise a classification changed
   * elsewhere (or the legacy backfill being run) would never show up, and the refresh button would
   * quietly lie. Register/revoke keep the cache: those only add parkers, they never change existing ones.
   */
  async refreshRegistry(): Promise<void> {
    this.classificationCache.clear();
    await this.loadSupplierRegistry();
  }

  async loadSupplierRegistry(): Promise<void> {
    const space = this.parkingSpace();
    if (!space?.id) return;

    const token = ++this.registryLoadToken;
    this.isLoadingRegistry.set(true);
    try {
      const base: ListParkingRightsRequest = {
        allowedParkingSpaces: [space.id],
        includeDetails: true,
        includeDeleted: this.showRevoked(),
        pageIndex: 0,
        pageSize: REGISTRY_FETCH_SIZE
      };

      const term = this.registrySearch().trim();
      this.lastServerSearch = term;

      const rights = await this.fetchRegistryRights(base, term);
      if (token !== this.registryLoadToken) return;

      const entries = rights
        .filter(r => r.parkingType?.type?.type === ParkingTypeEnum.LongTerm)
        .map(r => this.toRegistryEntry(r));
      this.supplierRegistry.set(entries);
      this.registryPageIndex.set(0);

      // One bulk request for the whole set: names are what `filteredRegistry` searches on, so they
      // have to cover every fetched row, and `byIds` answers for all of them at once.
      await this.loadRegistryParkerNames();
      if (token !== this.registryLoadToken) return;

      // Classifications, by contrast, cost one request per parker - so only the rows actually on
      // screen are resolved. Paging fills in the rest, and the cache means each parker is asked
      // about once.
      await this.loadRegistryClassifications(space.id);
    } catch (e) {
      console.error('Error loading supplier registry:', e);
      this.snackBar.open(this.t.instant('hotline.msg.loadRegistryFailed'), 'Close', { duration: 4000 });
    } finally {
      if (token === this.registryLoadToken) this.isLoadingRegistry.set(false);
    }
  }

  /**
   * Resolves the person behind each row.
   *
   * The registry lists parking rights, and a member's right is named after their *company* — so
   * without this every employee of one company is an identical row and an operator cannot tell whom
   * a plate belongs to. The name lives on the parker, so it has to be fetched.
   *
   * One bulk request for all distinct parkers, unlike the per-parker classification lookup below:
   * `byIds` accepts the whole set, so there is no reason to fan out here.
   *
   * A failure leaves the names blank and the registry otherwise working — the company, plates and
   * AK number still identify the row.
   */
  private async loadRegistryParkerNames(): Promise<void> {
    const parkerIds = [...new Set(
      this.supplierRegistry().map(e => e.parkerId).filter((id): id is string => !!id)
    )];
    if (parkerIds.length === 0) {
      return;
    }

    try {
      const parkers = await firstValueFrom(this.parkerService.operatorParkerByIdsPost({ parkerIds }));

      // firstName/lastName are newly added to ParkerDto (they used to sit on RegisteredParkerDto,
      // where serialization dropped them). Read regen-safely so this compiles until
      // `npm run generate:api:operator` runs, mirroring the operatorId pattern used elsewhere here.
      const nameById = new Map<string, string>();
      for (const parker of (parkers ?? []) as (ParkerDto & { firstName?: string; lastName?: string })[]) {
        if (!parker.id) continue;
        const name = [parker.firstName, parker.lastName]
          .map(part => part?.trim())
          .filter(Boolean)
          .join(' ');
        if (name) nameById.set(parker.id, name);
      }

      this.supplierRegistry.update(entries => entries.map(e => ({
        ...e,
        parkerName: e.parkerId ? nameById.get(e.parkerId) ?? '' : ''
      })));
    } catch (e) {
      console.error('Could not resolve parker names for the registry:', e);
    }
  }

  /**
   * Resolves each row's site classification.
   *
   * TEMPORARY — this is an N+1: one request per distinct parker, because the Parker contract only
   * exposes `...SiteClassificationCurrentGet(parkerId, spaceId)`. Acceptable only because the
   * registry is already capped at 100 rows (JKU: 31). The real fix is task 21 option A′ — a
   * `ListParkersBySiteClassification(spaceId, classifications)` query, so the backend filters and
   * pages in SQL and this method disappears entirely.
   *
   * Failures are swallowed per parker: an unresolved classification leaves the row unclassified
   * rather than blanking the whole registry.
   */
  private async loadRegistryClassifications(spaceId: string): Promise<void> {
    // The VISIBLE page, not everything fetched. The registry pulls up to 100 rights and shows 5,
    // and this lookup costs one database round trip per parker - resolving all of them turned a
    // single keystroke into ~100 requests and overloaded the testing database.
    const parkerIds = [...new Set(
      this.pagedRegistry().map(e => e.parkerId).filter((id): id is string => !!id)
    )];

    // Only ask about parkers we have not resolved yet. The registry reloads after every
    // register/revoke, and paging re-enters here, so without this cache each of those would ask again.
    const unknown = parkerIds.filter(id => !this.classificationCache.has(this.cacheKey(id, spaceId)));

    for (const batch of this.chunk(unknown, CLASSIFICATION_FETCH_CONCURRENCY)) {
      const resolved = await Promise.all(batch.map(async parkerId => {
        try {
          const dto = await firstValueFrom(
            this.classificationService.operatorParkerParkerIdSiteClassificationCurrentGet(parkerId, spaceId)
          );
          return { parkerId, classification: dto?.classification ?? null };
        } catch {
          // A parker with no classification 404s — that is "unclassified", not an error worth
          // surfacing. Cached as null so it is not retried on every reload.
          return { parkerId, classification: null };
        }
      }));
      for (const r of resolved) {
        this.classificationCache.set(this.cacheKey(r.parkerId, spaceId), r.classification);
      }
    }

    // Applied across the whole set: rows resolved on an earlier page keep their badge, and rows not
    // resolved yet are left untouched until they are paged into view.
    this.supplierRegistry.update(entries => entries.map(e => ({
      ...e,
      classification: e.parkerId && this.classificationCache.has(this.cacheKey(e.parkerId, spaceId))
        ? this.classificationCache.get(this.cacheKey(e.parkerId, spaceId)) ?? null
        : e.classification
    })));
  }

  /** Classification is per (parker, space), so the cache key must carry both. */
  private cacheKey(parkerId: string, spaceId: string): string {
    return `${parkerId}|${spaceId}`;
  }

  private chunk<T>(items: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
  }

  onClassificationFilter(value: ClassificationFilter): void {
    this.classificationFilter.set(value);
    this.registryPageIndex.set(0);
  }

  /** i18n key for a classification chip/badge — enum-driven, so new values need only a JSON key. */
  classificationLabelKey(classification: ParkerClassification): string {
    return `classification.${classification}`;
  }

  private toRegistryEntry(right: ParkingRightDto): SupplierRegistryEntry {
    const plateRefs = (right.allowedLicensePlates ?? [])
      .map(p => ({ id: p.licensePlateDto?.id ?? '', fullNumber: p.licensePlateDto?.fullNumber ?? '' }))
      .filter(p => !!p.id && !!p.fullNumber);
    const plates = plateRefs.map(p => p.fullNumber);
    if (plates.length === 0) plates.push('—');
    const comment = right.comment?.trim() || '';
    const timeType = right.parkingRightType as TimeBasedParkingRightDto | undefined;
    // Zone names, not a count — "Zone A, Zone B" tells the operator what the right actually covers.
    // Falls back to the unnamed-zone label so a nameless zone never renders as a blank gap.
    const zoneNames = (right.allowedParkingZones ?? [])
      .map(z => z.zoneDto?.name?.trim() || this.t.instant('hotline.unnamedZone'))
      .filter((n): n is string => !!n);
    return {
      id: right.id ?? '',
      supplierName: right.name?.trim() || 'Unnamed supplier',
      plates,
      plateRefs,
      comment,
      akNumber: extractAkNumber(comment),
      validFrom: timeType?.validFrom ?? null,
      validTo: timeType?.validTo ?? null,
      zoneIds: (right.allowedParkingZones ?? [])
        .map(z => z.parkingZoneId ?? '')
        .filter((id): id is string => !!id),
      zonesLabel: zoneNames.length > 0 ? zoneNames.join(', ') : this.t.instant('hotline.allZones'),
      operatorLabel: this.resolveOperatorLabel(right),
      isActive: right.isActive ?? false,
      isRevoked: right.isDeleted ?? false,
      parkerId: right.purchasedByParker ?? null,
      organizationId: right.purchasedByOrganizationId ?? null,
      parkerName: '',        // filled in by loadRegistryParkerNames()
      classification: null   // filled in by loadRegistryClassifications()
    };
  }

  // operatorId is a newly added field on the parking right; read it regen-safely so the
  // operator-app keeps compiling until the API client is regenerated.
  private resolveOperatorLabel(right: ParkingRightDto): string {
    const operatorId = (right as ParkingRightDto & { operatorId?: string | null }).operatorId;
    if (!operatorId) return 'System';
    // Rights granted by the logged-in operator resolve to their own name.
    const current = this.currentOperator();
    if (current?.id === operatorId) {
      const name = [current.firstName, current.lastName].filter(Boolean).join(' ').trim();
      if (name) return name;
    }
    // Fall back to the space creator's name for rights granted by whoever set up the space.
    const space = this.parkingSpace();
    if (space?.createdById === operatorId && space?.createdBy) {
      const name = [space.createdBy.firstName, space.createdBy.lastName].filter(Boolean).join(' ').trim();
      if (name) return name;
    }
    return `Operator ${operatorId.substring(0, 8)}`;
  }

  /**
   * The rows behind the registry.
   *
   * With no search term this is simply the first page of the space's rights — enough to browse.
   * With one it is a *server-side* search, because the hotline's real job is "a long-term parker is
   * on the phone, find them by plate": filtering one fetched page in the browser finds only the
   * handful of parkers that page happened to contain, which on a site with thousands of long-term
   * rights looks exactly like "it only shows suppliers".
   *
   * Two calls are needed rather than one. `searchTerm` matches name and comment (where the AK
   * number lives); the plate lives in another bounded context, so it is resolved to plate ids first
   * and passed as `allowedLicensePlates`. The spec ANDs its filters, so the two cannot be sent
   * together — they are unioned here instead.
   */
  private async fetchRegistryRights(
    base: ListParkingRightsRequest,
    term: string
  ): Promise<ParkingRightDto[]> {
    if (!term) {
      const result = await firstValueFrom(this.parkingRightService.parkingRightsFilterPost(base));
      return result?.items ?? [];
    }

    const plateIds = await this.resolvePlateIds(term);

    const [byText, byPlate] = await Promise.all([
      firstValueFrom(this.parkingRightService.parkingRightsFilterPost({ ...base, searchTerm: term }))
        .then(r => r?.items ?? []),
      plateIds.length === 0
        ? Promise.resolve([] as ParkingRightDto[])
        : firstValueFrom(this.parkingRightService.parkingRightsFilterPost({ ...base, allowedLicensePlates: plateIds }))
          .then(r => r?.items ?? [])
    ]);

    // Union, first occurrence wins — a right that matches both name and plate is one row.
    const merged = new Map<string, ParkingRightDto>();
    for (const right of [...byText, ...byPlate]) {
      if (right.id) merged.set(right.id, right);
    }
    return [...merged.values()];
  }

  /**
   * Plate text to plate ids. The term is normalized first: plates are stored without separators, so
   * a hyphen typed by the operator would otherwise match nothing at all.
   */
  private async resolvePlateIds(term: string): Promise<string[]> {
    const plateTerm = normalizePlate(term);
    if (plateTerm.length < MIN_PLATE_SEARCH_LENGTH) return [];

    try {
      const request: ListLicensePlatesRequest = {
        fullNumber: plateTerm,
        includeDeleted: this.showRevoked(),
        pageIndex: 0,
        pageSize: PLATE_LOOKUP_SIZE
      };
      const result = await firstValueFrom(this.licensePlateService.operatorLicensePlateFilterPost(request));
      return (result?.items ?? [])
        .map(lp => lp.id)
        .filter((id): id is string => !!id);
    } catch (e) {
      // A failed plate lookup must not take the whole registry down with it — the name/comment
      // half of the search still has an answer worth showing.
      console.error('Error resolving license plates for registry search:', e);
      return [];
    }
  }

  // ---- Registry search & pagination ----

  /**
   * A plate lives in another bounded context (see `fetchRegistryRights`), so it can only be found on
   * a site with more rights than fit on one page by actually asking the server - filtering the
   * loaded page locally silently misses it. This debounces instead of requiring Enter: an operator on
   * the phone reciting a plate must see it appear as they finish typing, not after an extra keypress.
   * The debounce (600 ms), the minimum length below, and cancelling a stale response
   * (`registryLoadToken`) are what keep that affordable.
   */
  onRegistrySearch(value: string): void {
    this.registrySearch.set(value);
    this.registryPageIndex.set(0);

    if (this.searchDebounce) clearTimeout(this.searchDebounce);

    const term = value.trim();
    if (term.length > 0 && term.length < MIN_SERVER_SEARCH_LENGTH) {
      // Too short to be worth a query; `filteredRegistry` still narrows the loaded page while the
      // operator keeps typing.
      return;
    }

    this.searchDebounce = setTimeout(() => {
      this.searchDebounce = null;
      void this.submitRegistrySearch();
    }, REGISTRY_SEARCH_DEBOUNCE_MS);
  }

  /** Runs the server-side search. Also bound to Enter, so an operator can skip the debounce wait. */
  async submitRegistrySearch(): Promise<void> {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }

    const term = this.registrySearch().trim();
    if (term.length > 0 && term.length < MIN_SERVER_SEARCH_LENGTH) return;

    // Same term already answered for - a repeat Enter is a no-op, not a repeat query.
    if (term === this.lastServerSearch) return;

    await this.loadSupplierRegistry();
  }

  registryPrevPage(): void {
    this.registryPageIndex.update(i => Math.max(0, i - 1));
    void this.resolveVisibleClassifications();
  }

  registryNextPage(): void {
    this.registryPageIndex.update(i => Math.min(this.registryTotalPages() - 1, i + 1));
    void this.resolveVisibleClassifications();
  }

  /**
   * Fills in the classification badges for the page just paged into view. Cached, so paging back and
   * forth costs nothing after the first visit.
   */
  private async resolveVisibleClassifications(): Promise<void> {
    const spaceId = this.parkingSpace()?.id;
    if (!spaceId) return;
    await this.loadRegistryClassifications(spaceId);
  }

  async registerSupplier(): Promise<void> {
    const space = this.parkingSpace();
    if (!space?.id) {
      this.snackBar.open(this.t.instant('hotline.msg.spaceMissing'), 'Close', { duration: 3000 });
      return;
    }
    const operatorId = this.currentOperator()?.id;
    if (!operatorId) {
      this.snackBar.open(this.t.instant('hotline.msg.spaceMissing'), 'Close', { duration: 3000 });
      return;
    }
    const name = this.supplierName().trim();
    const plate = this.supplierPlate().trim().toUpperCase();
    plate.replace(/\s+/g, ''); // remove whitespace
    // remove - and _ characters, since the parker service normalizes them away
    plate.replace(/[-_]/g, '');

    if (!name) {
      this.snackBar.open(this.t.instant('hotline.msg.enterName'), 'Close', { duration: 3000 });
      return;
    }
    if (!plate) {
      this.snackBar.open(this.t.instant('hotline.msg.enterPlate'), 'Close', { duration: 3000 });
      return;
    }

    this.isRegisteringSupplier.set(true);
    try {
      // 1) Register / resolve the supplier license plate
      const licensePlate = await firstValueFrom(
        this.parkerService.parkerRegisterLicensePlatePost({ fullNumber: plate })
      );
      if (!licensePlate?.id) {
        throw new Error('License plate registration returned no id');
      }
      // The right is bought by the plate's owner. With several current owners any pick is a guess,
      // and a wrong one files the right under someone else's name in the registry.
      const ownerIds = licensePlate.parkersIDs ?? [];
      if (ownerIds.length > 1) {
        this.snackBar.open(this.t.instant('hotline.msg.plateHasSeveralOwners'), 'Close', { duration: 5000 });
        return;
      }

      // 2) Ensure a Long-Term parking type exists for this space, owned by the acting operator
      const parkingTypeId = await this.ensureLongTermType(space, operatorId);

      // 3) Create the long-term parking right for the supplier
      const validTo = this.supplierValidTo()
        ? new Date(this.supplierValidTo()).toISOString()
        : null;
      const typeDto: TimeBasedParkingRightDto = {
        type: ParkingRightTypeEnum.TimeBased,
        validFrom: null,
        validTo,
        startTime: null,
        endTime: null,
        validDays: null,
        isAllDay: true,
        isRecurring: false
      };
      const zoneIds = this.selectedZoneIds();
      const comment = this.supplierComment().trim();
      // operatorId is newly added to CreateParkingRightRequest; the intersection keeps this
      // compiling against the not-yet-regenerated API client.
      const request: CreateParkingRightRequest & { operatorId?: string | null } = {
        typeDto,
        parkingTypeId,
        allowedParkingSpaces: [space.id],
        allowedLicensePlates: [licensePlate.id],
        allowedParkingZones: zoneIds.length ? zoneIds : null,
        purchasedByParker: ownerIds.at(0) ?? null,
        purchasedByOrganizationId: null,
        paymentReferenceId: null,
        name,
        comment: comment || null,
        operatorId
      };
      await firstValueFrom(this.parkingRightService.parkingRightsPost(request));

      this.snackBar.open(this.t.instant('hotline.msg.registered'), 'Close', { duration: 3000 });
      this.supplierName.set('');
      this.supplierPlate.set('');
      this.supplierComment.set('');
      this.supplierValidTo.set('');
      this.selectedZoneIds.set([]);
      await this.loadSupplierRegistry();
    } catch (e) {
      console.error('Error registering supplier:', e);
      this.snackBar.open(this.t.instant('hotline.msg.registerFailed') + ' ' + e, 'Close', { duration: 4000 });
    } finally {
      this.isRegisteringSupplier.set(false);
    }
  }

  private async ensureLongTermType(space: ParkingSpaceDto, operatorId: string): Promise<string> {
    const listRequest: ListLongTermParkersRequest = {
      availableForParkingSpaces: [space.id!],
      operatorId,
      includeDeleted: false,
      pageIndex: 0,
      pageSize: 1
    };
    const existing = await firstValueFrom(
      this.parkingTypeService.parkingTypesLongTermParkersFilterPost(listRequest)
    );
    const existingTypeId = existing?.items?.at(0)?.parkingTypeId;
    if (existingTypeId) return existingTypeId;

    const createRequest: CreateLongTermParkersRequest = {
      availableForParkingSpaces: [space.id!]
    };
    const created = await firstValueFrom(
      this.parkingTypeService.parkingTypesLongTermParkersPost(operatorId, createRequest)
    );
    const createdTypeId = created?.parkingTypeId;
    if (!createdTypeId) {
      throw new Error('Long-term parking type creation returned no parkingTypeId');
    }
    return createdTypeId;
  }

  /**
   * Opens the in-place editor. This is the fix for the delete-and-recreate workflow: adding a
   * second plate to a company no longer requires revoking it first, which is what left
   * soft-deleted rights behind and re-bound plates to arbitrary parkers.
   */
  async editSupplier(entry: SupplierRegistryEntry): Promise<void> {
    const dialogRef = this.dialog.open<EditSupplierDialogComponent, EditSupplierDialogData, boolean>(
      EditSupplierDialogComponent,
      {
        width: '560px',
        maxWidth: '95vw',
        data: {
          parkingRightId: entry.id,
          supplierName: entry.supplierName,
          comment: entry.comment,
          validFrom: entry.validFrom,
          validTo: entry.validTo,
          plates: entry.plateRefs,
          zoneIds: entry.zoneIds,
          availableZones: this.availableZones(),
          parkerId: entry.parkerId,
          organizationId: entry.organizationId
        }
      });

    const saved = await firstValueFrom(dialogRef.afterClosed());
    if (saved) {
      await this.loadSupplierRegistry();
    }
  }

  async revokeSupplier(entry: SupplierRegistryEntry): Promise<void> {
    if (!entry.id) return;
    if (!confirm(this.t.instant('hotline.msg.revokeConfirm', { name: entry.supplierName, plate: entry.plates.join(', ') }))) return;
    this.revokingId.set(entry.id);
    try {
      await firstValueFrom(this.parkingRightService.parkingRightsParkingRightIdDelete(entry.id));
      this.snackBar.open(this.t.instant('hotline.msg.revoked'), 'Close', { duration: 3000 });
      await this.loadSupplierRegistry();
    } catch (e) {
      console.error('Error revoking supplier:', e);
      this.snackBar.open(this.t.instant('hotline.msg.revokeFailed'), 'Close', { duration: 4000 });
    } finally {
      this.revokingId.set('');
    }
  }

  /**
   * Reverses a revoke. Only reachable while "Show revoked" is on, which is the only way a revoked
   * company is visible at all.
   */
  async restoreSupplier(entry: SupplierRegistryEntry): Promise<void> {
    if (!entry.id) return;
    if (!confirm(this.t.instant('hotline.msg.restoreConfirm',
      { name: entry.supplierName, plate: entry.plates.join(', ') }))) return;
    this.restoringId.set(entry.id);
    try {
      // POST parkingrights/{id}/restore is newly added; the cast keeps this compiling against the
      // not-yet-regenerated API client (same approach as operatorId on CreateParkingRightRequest).
      // Run `npm run generate:api:operator` and the cast becomes a no-op.
      const api = this.parkingRightService as ParkingRightService & {
        parkingRightsParkingRightIdRestorePost(parkingRightId: string): Observable<string>;
      };
      await firstValueFrom(api.parkingRightsParkingRightIdRestorePost(entry.id));
      this.snackBar.open(this.t.instant('hotline.msg.restored'), 'Close', { duration: 3000 });
      await this.loadSupplierRegistry();
    } catch (e) {
      console.error('Error restoring supplier:', e);
      this.snackBar.open(this.t.instant('hotline.msg.restoreFailed'), 'Close', { duration: 4000 });
    } finally {
      this.restoringId.set('');
    }
  }
}
